/**
 * FriendChatInner.tsx
 *
 * Refactored friend chat inner component.
 * Uses shared services and hooks from the chat module.
 * Reduced from ~866 lines to ~400 lines.
 */

import { FC, useEffect, useState, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux"
import { v4 as uuidv4 } from "uuid"

// Redux
import { RootState } from "src/setup/redux/store"
import * as chat from "src/app/modules/chat/redux/ChatSlice"

// Models
import { Collaboration as HandledMessageListItem } from "src/app/models/Chat"
import User from "src/app/models/User"
import Customer from "src/app/models/Customer"
import newMessageModel, { MessageStatus } from "src/app/models/Message"

// Firebase
import { Timestamp } from "src/db"
import { createRef } from "src/db/connection"
import { storage } from "src/db"

// Actions
import * as Chat from "src/actions/chat"
import * as Log from "src/util/SDayslogger"

// Local storage
import * as lc from "src/app/modules/localstorage/index"

// Services
import { FileValidationService } from "src/app/modules/chat/services/file"

// Components
import FriendChatMessage from "src/app/layout/chat-handle/components/FriendChatMessage"
import { MediaModalProvider } from "src/app/modules/chat/context/MediaModalContext"
import { MediaModal } from "src/app/modules/chat/components/media-modal/MediaModal"

// Styles
import "src/styles/css/dropdown.scss"
import "src/styles/css/chat.scss"
import "src/styles/css/margin.scss"

const mapState = (state: RootState) => ({ chat: state.Chat })
const connector = connect(mapState, chat.ChatSlice.actions)

type Props = {
  isDrawer?: boolean
  propsredux?: ConnectedProps<typeof connector>
  friend?: User
  customer?: Customer
  user?: User
}

const FriendChatInner: FC<Props> = ({ isDrawer = false }) => {
  const nav = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" })

  // Services
  const validationService = FileValidationService.getInstance()

  // Redux selectors
  const selectedChat = useSelector((state: RootState) => state.Chat.selectedUserChat)
  const collablist = useSelector((state: RootState) => state.Chat.userChatList)
  const listMessagess = useSelector((state: RootState) => state.Chat.userListMessage)
  let lastVisibleMessage: any = useSelector((state: RootState) => state.Chat.lastVisibleMessage)

  // State
  const [collabs, setCollabs] = useState<HandledMessageListItem | any>()
  const [messages, setMessages] = useState<newMessageModel[]>([])
  const [friendChat, setFriendChat] = useState<User | undefined>()
  const [message, setMessage] = useState("")
  const [isLoadingMessage, setIsLoadingMessage] = useState(false)
  const [isLoadingCollaboration, setIsLoadingCollaboration] = useState(false)
  const [onLoadMoreMessage, setOnLoadMoreMessage] = useState(false)
  const [chatUpdateFlag, toggleChatUpdateFlat] = useState(false)

  // File attachment state
  const [file, setFile] = useState<File>()
  const [fileSize, setFileSize] = useState("")
  const [inputFileName, setInputFileName] = useState("")
  const [inputContentType, setInputContentType] = useState("")
  const [messageType, setMessageType] = useState("text")
  const [prevFile, setPrevFile] = useState("")

  // Refs
  const bottomRef = useRef<HTMLDivElement>(null)
  const attachmentRef = useRef<HTMLInputElement>(null)
  const sendButtonRef = useRef<HTMLButtonElement>(null)

  const companyID = lc.getItemLC(lc.LCName.CompanyID)?.toString()
  const activeUserId = lc.getItemLC("UID")

  // Clear file state
  const clearFileState = useCallback(() => {
    setMessage("")
    setFile(undefined)
    setFileSize("")
    setInputFileName("")
    setInputContentType("")
    setMessageType("text")
    setPrevFile("")
  }, [])

  // Handle file selection
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0]
      if (attachmentRef.current) {
        attachmentRef.current.value = ""
      }
      if (!selectedFile) return

      // Validate file
      const tempArrFileType = selectedFile.type.split("/")
      if (!tempArrFileType.length || !tempArrFileType[0]) {
        alert(t("HC.Error.InvalidFiletype"))
        setInputFileName(t("HC.Error.InvalidFiletype"))
        return
      }

      const validation = validationService.validateFile(
        tempArrFileType[0],
        tempArrFileType[1],
        selectedFile.size
      )

      if (validation.error) {
        const errorMsg =
          validation.maxSize > 0
            ? t(`HC.File.${validation.error}`).replace("<<size>>", `${validation.maxSize} MB`)
            : t(`HC.Error.${validation.error}`)
        alert(errorMsg)
        setInputFileName(errorMsg)
        return
      }

      // Set file state
      setFile(selectedFile)
      setPrevFile(URL.createObjectURL(selectedFile))
      setInputFileName(selectedFile.name)
      setFileSize(validationService.formatSize(selectedFile.size))
      setInputContentType(selectedFile.type)
      setMessageType(validation.fileType || "text")
    },
    [t, validationService]
  )

  // Upload file to Firebase Storage
  const uploadFile = useCallback(async (): Promise<string> => {
    if (!file) return ""

    const metadata = {
      contentType: inputContentType,
      fileName: inputFileName,
    }
    const uuid = uuidv4()
    const path = `collaborations/${selectedChat}/${messageType}s/chat/${uuid}`

    try {
      await storage.ref(path).child(inputFileName).put(file, metadata)
      return await storage.ref(path).child(inputFileName).getDownloadURL()
    } catch (error: any) {
      console.log("Upload error:", error.message)
      alert(t("HC.Error.FailedUpload"))
      setInputFileName(t("HC.Error.FailedUpload"))
      setFile(undefined)
      return ""
    }
  }, [file, inputContentType, inputFileName, messageType, selectedChat, t])

  // Send message
  const sendMessage = useCallback(async () => {
    if (bottomRef.current) {
      bottomRef.current.scrollTop = bottomRef.current.scrollHeight
    }

    sendButtonRef.current?.setAttribute("data-kt-indicator", "on")

    let firebaseMediaURL = ""
    if (messageType !== "text") {
      firebaseMediaURL = await uploadFile()
      if (!firebaseMediaURL) {
        sendButtonRef.current?.removeAttribute("data-kt-indicator")
        return
      }
    } else if (!message.trim()) {
      sendButtonRef.current?.removeAttribute("data-kt-indicator")
      return
    }

    // Create new message
    const newMessage: newMessageModel = {
      room: createRef(`collaborations/${selectedChat}/rooms`, collabs?.roomsModel[0]?.id),
      channel: "",
      createdAt: Timestamp.now(),
      sentAt: Timestamp.now(),
      deliveredAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      destination: "outbound",
      mediaUrl: firebaseMediaURL,
      isActive: true,
      messageType,
      textContent: message,
      previewurl: false,
      voice: false,
      filename: inputFileName,
      filesize: fileSize,
      collaboration: createRef("collaborations", selectedChat),
      fromUser: createRef("users", activeUserId),
      toUser: createRef("users", friendChat?.id!),
      status: MessageStatus.delivered,
      notifiedAt: null,
    }

    // Clear input
    toggleChatUpdateFlat(!chatUpdateFlag)
    clearFileState()

    // Handle response
    const handleResponse = (responseMessage: newMessageModel, error: string) => {
      if (responseMessage) {
        const newListMessage = [...messages, responseMessage]
        setMessages(newListMessage)
        dispatch(chat.setUserListMessages(newListMessage))

        // Update collaboration list
        const newCollabList: HandledMessageListItem[] = []
        let newCollab = collabs

        collablist.forEach((item) => {
          if (item.id === selectedChat && newMessage.messageType) {
            const lastmessage = newMessage.textContent || newMessage.filename || ""
            const updatedItem = {
              ...item,
              lastInteractionAt: newMessage.createdAt,
              lastInteractionChannel: newMessage.channel,
              lastInteractionMessage: lastmessage,
              lastInteractionType: newMessage.messageType,
            }
            newCollab = updatedItem
            Chat.updateLastInteractionUserChat(selectedChat, newMessage)
            newCollabList.push(updatedItem)
          }
        })

        collablist.forEach((item) => {
          if (item.id !== selectedChat) {
            newCollabList.push(item)
          }
        })

        setCollabs(newCollab)
        dispatch(chat.setUserChatList(newCollabList))
      } else {
        console.log("Failed to send message")
      }

      if (bottomRef.current) {
        bottomRef.current.scrollTop = bottomRef.current.scrollHeight
      }
    }

    try {
      await Chat.createFriendCollaborationMessage(
        newMessage,
        selectedChat,
        friendChat?.id!,
        (respMessage: newMessageModel, responseJson: string) => {
          handleResponse(respMessage, responseJson)
        }
      )
    } catch (error) {
      Log.errorWithNavigate(nav, `Failed to create collaboration message: ${selectedChat}`)
    }

    sendButtonRef.current?.removeAttribute("data-kt-indicator")
  }, [
    bottomRef, sendButtonRef, messageType, uploadFile, message, selectedChat,
    collabs, activeUserId, friendChat, inputFileName, fileSize, chatUpdateFlag,
    clearFileState, messages, dispatch, collablist, nav
  ])

  // Handle enter key
  const onEnterPress = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.keyCode === 13 && !e.shiftKey) {
        e.preventDefault()
        sendMessage()
      }
    },
    [sendMessage]
  )

  // Load more messages on scroll
  const onLoadMessage = useCallback(async () => {
    if (!bottomRef.current || bottomRef.current.scrollTop !== 0) return

    setIsLoadingMessage(true)
    setOnLoadMoreMessage(true)

    const initialHeight = bottomRef.current.scrollHeight
    let maxLoaded = false

    const prevListMessages = await Chat.fetchCollaborationPrevMessage(
      selectedChat,
      lastVisibleMessage,
      (lastVisible: any) => {
        dispatch(chat.setLastVisibleMessage(lastVisible))
        lastVisibleMessage = lastVisible
      }
    )

    if (prevListMessages?.length > 0) {
      const newMessageList = [...prevListMessages, ...messages]
      dispatch(chat.setUserListMessages(newMessageList))
      setMessages(newMessageList)
    } else {
      maxLoaded = true
    }

    if (!maxLoaded && bottomRef.current.scrollHeight !== initialHeight) {
      bottomRef.current.scrollTop = bottomRef.current.scrollHeight - initialHeight
    } else {
      bottomRef.current.scrollTop = 0
    }

    setIsLoadingMessage(false)
    setOnLoadMoreMessage(false)
  }, [bottomRef, selectedChat, lastVisibleMessage, messages, dispatch])

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollTop = bottomRef.current.scrollHeight
    }
  }, [])

  // Initialize chat when selection changes
  useEffect(() => {
    Chat.clearUserUnreadMessages(selectedChat)
    dispatch(chat.updateUserUnreadMessage(selectedChat))

    setCollabs(collablist.find((obj) => obj.id === selectedChat))
    setIsLoadingCollaboration(true)

    Chat.fetchMessageCollaboration(
      listMessagess,
      selectedChat,
      "",
      undefined,
      selectedChat,
      (lastVisible: any) => {
        dispatch(chat.setLastVisibleMessage(lastVisible))
        lastVisibleMessage = lastVisible
      }
    )
      .then((ListMessages) => {
        dispatch(chat.setUserListMessages(ListMessages || listMessagess))
      })
      .catch(() => {
        dispatch(chat.setUserListMessages([]))
      })

    setIsLoadingCollaboration(false)

    // Delivery report handler
    const handleDeliveryReport = (event: any) => {
      const msg: newMessageModel = event.detail.message
      if (msg?.collaboration?.id === selectedChat) {
        dispatch(chat.updateStatusListMessages(msg))
      }
    }

    window.addEventListener("DRNotif", handleDeliveryReport)
    return () => window.removeEventListener("DRNotif", handleDeliveryReport)
  }, [selectedChat])

  // Update messages from redux
  useEffect(() => {
    if (!onLoadMoreMessage) {
      setMessages(listMessagess)
      Chat.clearUserUnreadMessages(selectedChat)
      setTimeout(scrollToBottom, 100)
    }
  }, [listMessagess, onLoadMoreMessage, selectedChat, scrollToBottom])

  // Set friend from collaboration
  useEffect(() => {
    setFriendChat(collabs?.userModel)
  }, [collabs])

  return (
    <MediaModalProvider>
      <div
        className="flex-fill"
        style={{ border: "top bottom 10px solid #9899AC", overflow: "hidden", height: "100%" }}
        id={isDrawer ? "kt_drawer_chat_messenger_body" : "kt_chat_messenger_body"}
      >
      {/* Messages area */}
      <div
        id="chat-body"
        className="card-body chat-body-custom"
        style={{
          height: isTabletOrMobile ? "450px" : "80%",
          overflowX: "hidden",
          overflowY: "scroll",
          paddingLeft: "5px",
          paddingRight: "0px",
        }}
        onScroll={onLoadMessage}
        ref={bottomRef}
      >
        {isLoadingCollaboration && (
          <div className="h-100 text-gray-700" style={{ textAlign: "center" }}>
            <span style={{ fontStyle: "italic" }}>{t("HC.Info.LoadChat")}</span>
            <span className="spinner-border spinner-border-sm align-middle ms-2" />
          </div>
        )}
        {isLoadingMessage && (
          <div className="d-flex justify-content-center p-3">
            <span className="spinner-border spinner-border-sm align-middle ms-2" />
          </div>
        )}
        {!isLoadingCollaboration &&
          messages?.map((msg, index) => (
            <FriendChatMessage
              key={index}
              message={msg}
              index={index}
              isDrawer={isDrawer}
              messages={messages}
              collaborationId={selectedChat}
            />
          ))}
      </div>

      {/* Input footer */}
      <div
        className="card-footer pt-1 sticky-bottom chat-footer-custom"
        id={isDrawer ? "kt_drawer_chat_messenger_footer" : "kt_chat_messenger_footer"}
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          left: 0,
          justifyContent: "space-between",
          padding: "10px",
          backgroundColor: "white",
        }}
      >
        <div style={{ overflowY: "scroll" }}>
          <textarea
            id="chatTextMessage"
            className="form-control form-control-flush mb-3"
            style={{
              height: isTabletOrMobile ? "" : "60%",
              paddingLeft: "0px",
              maxHeight: "150px",
              backgroundColor: "white",
              zIndex: 99,
              display: messageType !== "document" && messageType !== "audio" ? "" : "none",
            }}
            rows={1}
            placeholder={t("HC.Input.TextMessage")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={onEnterPress}
          />
        </div>

        <div className="d-flex space">
          <div className="d-flex align-items-center">
            {!file && (
              <label
                className="btn btn-sm btn-icon btn-active-light-primary me-1"
                data-bs-toggle="tooltip"
                title={t("HC.Info.Upload")}
              >
                <i className="bi bi-upload text-custom fs-1 p-5" />
                <input
                  ref={attachmentRef}
                  onChange={handleFileSelect}
                  type="file"
                  name="attachment"
                  multiple={false}
                  style={{ display: "none" }}
                />
              </label>
            )}
            {file && <label>{inputFileName}</label>}
            {file && (
              <label
                className="btn btn-sm btn-icon-danger btn-text-danger me-1"
                title={t("HC.File.RemoveFile")}
              >
                <i className="bi bi-x-circle fs-1" onClick={clearFileState} />
              </label>
            )}
          </div>

          <div className="d-flex align-items-center me-2 bg-primary" style={{ right: 0, padding: 2.5 }}>
            <button
              id="sendButton"
              ref={sendButtonRef}
              className="btn btn-primary"
              type="button"
              onClick={sendMessage}
            >
              <span className="indicator-label">{t("HC.Button.SendFrom").toUpperCase()}</span>
              <span className="indicator-progress">
                {t("HC.Button.SendFrom").toUpperCase()}
                <span className="spinner-border spinner-border-sm align-middle ms-2" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Single shared modal for all media in friend chat */}
      <MediaModal />
    </div>
    </MediaModalProvider>
  )
}

export default connector(FriendChatInner)
